import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, finalize, map, of } from 'rxjs';
import { PbxSocket } from '@app/main/socket-io/pbx-socket.service';
import { ClientPresenceDto, ClientPresenceEvents } from '@app/core/types/client-presence-events';
import { Client } from '@app/core/types/client';
import { BrandCodes } from '@app/core/types/brand';

export type ClientPresenceState = 'online' | 'offline' | 'unknown' | 'notTracked';

// TODO dev-3085: remove when the other brands' websites also open the client-communication /clients socket.
const TRACKED_BRANDS: string[] = [BrandCodes.SW];

// Live "client has the website open" state, pushed by client-communication.
// Rides on admin's existing client-communication connection (registered as PbxSocket) rather than opening a
// second one - the PBX gateway would otherwise treat it as another operator connection.
// Clients on screen are asked about once (batched), then kept current by client:presence events.
// Unknown while the socket is disconnected - callers should then show that, not "offline".
@Injectable({ providedIn: 'root' })
export class ClientPresenceService {
  private static readonly MAX_CHECK_BATCH = 100;

  private readonly online = new BehaviorSubject<Map<number, boolean>>(new Map());
  private readonly watched = new Map<number, number>(); // userId -> number of subscribers
  private readonly pending = new Set<number>();
  private flushScheduled = false;

  constructor(private socket: PbxSocket) {
    socket
      .fromEvent<ClientPresenceDto>(ClientPresenceEvents.PRESENCE)
      .subscribe(({ userId, online }) => this.update({ [userId]: online }));
    socket.on('connect', () => this.requestCheck([...this.watched.keys()]));
    socket.on('disconnect', () => this.online.next(new Map()));
  }

  /** What to show for a client: live state for tracked brands, 'notTracked' for the rest. */
  state$(client: Client): Observable<ClientPresenceState> {
    if (!TRACKED_BRANDS.includes(client?.brand?.code)) {
      return of('notTracked');
    }
    return this.online$(client.id).pipe(
      map((online) => (online === true ? 'online' : online === false ? 'offline' : 'unknown'))
    );
  }

  /** true / false once known; undefined while unknown (e.g. socket not connected yet). */
  online$(userId: number): Observable<boolean | undefined> {
    return new Observable<boolean | undefined>((subscriber) => {
      this.watched.set(userId, (this.watched.get(userId) ?? 0) + 1);
      if (!this.online.value.has(userId)) {
        this.requestCheck([userId]);
      }
      return this.online.pipe(map((states) => states.get(userId))).subscribe(subscriber);
    }).pipe(
      distinctUntilChanged(),
      finalize(() => this.unwatch(userId))
    );
  }

  private unwatch(userId: number) {
    const count = (this.watched.get(userId) ?? 1) - 1;
    if (count > 0) {
      this.watched.set(userId, count);
    } else {
      this.watched.delete(userId);
    }
  }

  // Many components ask at once (e.g. a list page) - collect them into one request per tick.
  private requestCheck(userIds: number[]) {
    userIds.forEach((id) => this.pending.add(id));
    if (this.flushScheduled || !this.socket.socketConnected) {
      return; // sent on 'connect' if not connected yet
    }
    this.flushScheduled = true;
    setTimeout(() => {
      this.flushScheduled = false;
      const ids = [...this.pending];
      this.pending.clear();
      for (let i = 0; i < ids.length; i += ClientPresenceService.MAX_CHECK_BATCH) {
        const userIds = ids.slice(i, i + ClientPresenceService.MAX_CHECK_BATCH);
        this.socket.ioSocket.emit(ClientPresenceEvents.CHECK, { userIds }, (states: Record<number, boolean>) =>
          this.update(states)
        );
      }
    });
  }

  private update(states: Record<number, boolean>) {
    const next = new Map(this.online.value);
    Object.entries(states).forEach(([id, online]) => next.set(Number(id), online));
    this.online.next(next);
  }
}
